import { useRef, useState } from "react";
import { UploadCloud, FileText, X, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { http, apiError } from "../../lib/api";

export const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp";
const EXTS = ACCEPT.split(",").map((e) => e.slice(1));
const MAX = 10 * 1024 * 1024;
const MAX_FILES = 10;

export const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const Row = ({ f, onRemove }) => (
  <li className="flex items-center gap-3 p-3 bg-[var(--ink)] border hairline" data-testid={`upload-file-${f.key}`}>
    <FileText className="w-5 h-5 text-gold shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-sm text-slate-100 truncate" data-testid="upload-file-name">{f.name}</p>
      <p className="text-xs text-slate-400">{fmtSize(f.size)}
        {f.state === "uploading" && <> · Uploading {f.progress}%</>}
        {f.state === "done" && <span className="text-emerald-300"> · Uploaded</span>}
      </p>
      {f.state === "uploading" && <div className="h-1 bg-white/10 mt-2"><div className="h-1 bg-[var(--gold)] transition-[width]" style={{ width: `${f.progress}%` }} /></div>}
      {f.state === "error" && <p className="text-xs text-rose-300 mt-1 flex items-start gap-1" data-testid="upload-file-error"><AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />{f.error}</p>}
    </div>
    {f.state === "uploading" ? <Loader2 className="w-4 h-4 animate-spin text-gold" /> : f.state === "done" ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : null}
    <button type="button" onClick={() => onRemove(f.key)} aria-label={`Remove ${f.name}`} className="p-1.5 text-slate-400 hover:text-rose-300" data-testid={`upload-remove-${f.key}`}><X className="w-4 h-4" /></button>
  </li>
);

export const FileUploader = ({ files, setFiles }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const update = (key, patch) => setFiles((cur) => cur.map((f) => (f.key === key ? { ...f, ...patch } : f)));

  const upload = async (entry, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await http.post("/opportunities/upload", fd, {
        onUploadProgress: (e) => update(entry.key, { progress: e.total ? Math.round((e.loaded / e.total) * 100) : 50 }),
      });
      update(entry.key, { state: "done", progress: 100, id: data?.id, token: data?.token });
    } catch (e) {
      update(entry.key, { state: "error", error: apiError(e) });
    }
  };

  const add = (list) => {
    const incoming = Array.from(list || []);
    const room = MAX_FILES - files.length;
    incoming.slice(0, Math.max(0, room)).forEach((file) => {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      const entry = { key: `${Date.now()}-${Math.random().toString(36).slice(2)}`, name: file.name, size: file.size, progress: 0, state: "uploading" };
      if (!EXTS.includes(ext)) Object.assign(entry, { state: "error", error: "This file type isn't accepted." });
      else if (file.size > MAX) Object.assign(entry, { state: "error", error: "Files must be 10 MB or smaller." });
      setFiles((cur) => [...cur, entry]);
      if (entry.state === "uploading") upload(entry, file);
    });
    return incoming.length > room;
  };
  const [tooMany, setTooMany] = useState(false);

  return (
    <div data-testid="file-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); setTooMany(add(e.dataTransfer.files)); }}
        className={`border border-dashed p-8 text-center transition-colors ${drag ? "border-[var(--gold)] bg-[rgba(212,175,55,0.06)]" : "border-white/25"}`}
        data-testid="upload-dropzone">
        <UploadCloud className="w-8 h-8 text-gold mx-auto" />
        <p className="text-slate-200 mt-3 text-sm">Drag and drop files here, or</p>
        <button type="button" onClick={() => input.current?.click()} className="btn-ghost !py-2.5 !px-5 mt-3" data-testid="upload-browse-button">Choose files</button>
        <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { setTooMany(add(e.target.files)); e.target.value = ""; }} data-testid="upload-file-input" />
        <p className="text-xs text-slate-400 mt-4 leading-relaxed">PDF, Word, Excel, PowerPoint, TXT, CSV, JPG, PNG, GIF or WEBP · up to 10 MB each · max {MAX_FILES} files (40 MB total). Files with macros aren't accepted.</p>
      </div>
      {tooMany && <p className="text-xs text-rose-300 mt-2" data-testid="upload-too-many">You can attach up to {MAX_FILES} files.</p>}
      {files.length > 0 && <ul className="mt-4 space-y-2" data-testid="upload-file-list">{files.map((f) => <Row key={f.key} f={f} onRemove={(k) => setFiles((cur) => cur.filter((x) => x.key !== k))} />)}</ul>}
    </div>
  );
};
